<script setup lang="ts" generic="T">
import { onClickOutside, useFocus } from "@vueuse/core";
import { cloneDeep, debounce } from "es-toolkit";
import { computed, ref, Ref, watch } from "vue";

import { useTypedIntl } from "@/locales/typed-intl";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  items?: T[];
  filterOn: keyof T;
  complete?(query: string): Promise<T[]>;
  clearable?: boolean;
  dropdown?: boolean;
  disabled?: boolean;
}

interface IEmits {
  (e: "complete", input: string): void;
  (e: "item-select", item: T): void;
  (e: "change", value?: T): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();

const autocomplete = ref();
const inputField = ref();

const model = defineModel<T>();

const input = ref<string>();
const isLoading = ref<boolean>(false);
const isSuggestionsVisible = ref<boolean>(false);
const suggestions = ref<T[]>([]) as Ref<T[]>;

const arrowSelectedItemIndex = ref<number | null>(null);

const { focused } = useFocus(inputField);

const inputExists = computed(() => {
  return Boolean(
    suggestions.value.find((item) => {
      return item[props.filterOn] === input.value;
    }),
  );
});

const handleInputChange = debounce(async () => {
  const query = input.value;

  if (!query) {
    isSuggestionsVisible.value = false;
    return;
  }

  if (props.complete) {
    try {
      isLoading.value = true;
      suggestions.value = await props.complete(query);
    } catch (e) {
      console.error(e);
    } finally {
      isLoading.value = false;
    }
  } else if (props.items) {
    suggestions.value = props.items.filter((item) => {
      const value = item[props.filterOn];
      return typeof value === "string" && value.toLowerCase().includes(query.trim().toLowerCase());
    });
  }

  emits("complete", query);

  isSuggestionsVisible.value = true;
  arrowSelectedItemIndex.value = null;
}, 350);

function handleItemSelect(selectedItem: T) {
  const result = suggestions.value.find((item) => {
    return item[props.filterOn] === selectedItem[props.filterOn];
  });

  if (!result) return;

  model.value = result;
  input.value = result[props.filterOn] as string;
  emits("item-select", selectedItem);

  isSuggestionsVisible.value = false;
  arrowSelectedItemIndex.value = null;
}

function handleKeyupEnter() {
  focused.value = false;
  isSuggestionsVisible.value = false;

  if (!inputExists.value && arrowSelectedItemIndex.value === null) reset();
}

function reset() {
  input.value = undefined;
  model.value = undefined;
  arrowSelectedItemIndex.value = null;

  if (props.items) {
    suggestions.value = cloneDeep(props.items);
  } else {
    suggestions.value = [];
  }
}

function handleArrowEvent(down: boolean) {
  if (!isSuggestionsVisible.value) return;

  const suggestionsLength = suggestions.value.length;

  if (arrowSelectedItemIndex.value === null) {
    // If user has just entered a value in the input field and no item has been selected yet, it goes to first (down) or last (up)
    arrowSelectedItemIndex.value = down ? 0 : suggestionsLength - 1;
  } else {
    // If user is already navigating through suggestions, it loops back to first item (down) if user reaches last item
    // or to last item if user is at the first item (up), else it increases or decreases by one
    arrowSelectedItemIndex.value =
      (arrowSelectedItemIndex.value + (down ? 1 : -1) + suggestionsLength) % suggestionsLength;
  }
}

function handleSubmitArrowSelectedItem() {
  if (arrowSelectedItemIndex.value === null) return;
  handleItemSelect(suggestions.value[arrowSelectedItemIndex.value]);
}

onClickOutside(autocomplete, () => {
  if (isSuggestionsVisible.value) isSuggestionsVisible.value = false;

  if (!inputExists.value) {
    reset();
  }
});

watch(model, (newValue) => {
  emits("change", newValue);

  if (!newValue) {
    reset();
  }
});

watch(
  () => props.items,
  (newValue) => {
    if (!newValue) {
      suggestions.value = [];
    } else {
      suggestions.value = cloneDeep(newValue);
    }
  },
  { immediate: true },
);
</script>

<template>
  <BaseInput>
    <div
      ref="autocomplete"
      @keydown.down="handleArrowEvent(true)"
      @keydown.up="handleArrowEvent(false)"
      @keyup.enter="handleSubmitArrowSelectedItem()"
    >
      <div
        class="relative flex"
        :class="{
          'p-inputwrapper-filled': !!input,
        }"
      >
        <input
          ref="inputField"
          v-model="input"
          class="p-component p-inputtext pr-10 truncate"
          :class="{
            'rounded-r-none': $props.dropdown,
          }"
          :disabled="$props.disabled"
          type="text"
          @input="handleInputChange()"
          @keyup.enter="handleKeyupEnter()"
        />

        <HospitalityButton
          v-if="$props.dropdown"
          class="!rounded-l-none shrink-0 !w-[40px]"
          :disabled="$props.disabled"
          size="sm"
          @click="isSuggestionsVisible = !isSuggestionsVisible"
        >
          <i :class="`pi pi-angle-down transition-all ${isSuggestionsVisible ? 'rotate-180' : ''} text-xl`" />
        </HospitalityButton>
      </div>

      <div
        v-show="isSuggestionsVisible"
        class="absolute bg-white border rounded-md min-w-full w-fit mt-2 z-10"
      >
        <div class="max-h-[20rem] overflow-auto">
          <ul
            v-if="suggestions.length"
            class="w-full"
          >
            <li
              v-for="(item, index) in suggestions"
              :key="index"
              class="flex flex-row items-center gap-2 hover:bg-gray-100 p-2 cursor-pointer"
              :class="{
                'bg-gray-100': arrowSelectedItemIndex === index,
              }"
              @click="handleItemSelect(item)"
              @mouseenter="arrowSelectedItemIndex = index"
            >
              <slot
                :index="index"
                :item="item"
                name="item"
              >
                <span class="px-2 py-1">
                  {{ item[props.filterOn] }}
                </span>
              </slot>
            </li>
          </ul>

          <div
            v-else
            class="p-4"
          >
            <span>
              {{ t(k.global.misc.noResult) }}
            </span>
          </div>
        </div>

        <slot name="suggestions-footer" />
      </div>

      <i
        v-if="isLoading"
        class="pi pi-spinner text-text-secondary animate-spin absolute mt-[13px] right-4"
      />
      <i
        v-if="!isLoading && $props.clearable && model"
        class="pi pi-times text-text-secondary absolute mt-[-30px] right-4 cursor-pointer"
        :class="{
          'mr-[45px]': $props.dropdown,
        }"
        @click="reset()"
      />
    </div>
  </BaseInput>
</template>
